import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { PageHeader, DataTable, EmptyState, ErrorState, StatusPill, FormDialog } from '@ui-kit';
import type { DataTableColumn } from '@ui-kit';
import { useFeedbackList } from '../hooks/useFeedbackList';
import { useAnswerFeedback } from '../hooks/useAnswerFeedback';
import { feedbackAnswerSchema, type FeedbackAnswerFormValues } from '../schema/feedbackSchema';
import type { FeedbackResponse, FeedbackStatus, FeedbackApiError } from '../types/feedback';

const PAGE_SIZE = 20;

const readOnlyBodyStyle: React.CSSProperties = {
  padding: 'var(--spacing-3)',
  background: 'var(--color-surface-muted)',
  border: '1px solid var(--color-surface-border)',
  borderRadius: 'var(--radius-md)',
  fontSize: 'var(--font-size-sm)',
  color: 'var(--color-text-secondary)',
  whiteSpace: 'pre-wrap',
  marginBottom: 'var(--spacing-4)',
};

const labelStyle: React.CSSProperties = {
  fontSize: 'var(--font-size-xs)',
  color: 'var(--color-text-secondary)',
  fontWeight: 600,
  display: 'block',
  marginBottom: 'var(--spacing-1)',
};

const textareaStyle: React.CSSProperties = {
  width: '100%',
  padding: 'var(--spacing-2) var(--spacing-3)',
  background: 'var(--color-surface-elevated)',
  border: '1px solid var(--color-surface-border)',
  borderRadius: 'var(--radius-md)',
  fontSize: 'var(--font-size-sm)',
  color: 'var(--color-text-primary)',
  fontFamily: 'var(--font-family-sans)',
  boxSizing: 'border-box',
  resize: 'vertical',
  minHeight: 120,
};

const textareaErrorStyle: React.CSSProperties = {
  ...textareaStyle,
  borderColor: 'var(--color-danger)',
};

const errorMsgStyle: React.CSSProperties = {
  fontSize: 'var(--font-size-xs)',
  color: 'var(--color-danger)',
  marginTop: 2,
};

const ellipsisStyle: React.CSSProperties = {
  display: 'block',
  maxWidth: 280,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};

/**
 * FeedbackAdminPage — 카탈로그 피드백/문의 관리 (queue #663 §UAT-2026-07-09-R663 §R663-3 ·
 * queue #684 §UAT-2026-07-14-R684 §R684-4(a) 확장 — 답변 다이얼로그 + 제출자 email + [E2E] 토글).
 * ROLE_ADMIN 전용(라우트 AdminRouteGuard 가 서버 신뢰원 GET /auth/me 기반으로 게이트).
 * 5상태: default(DataTable) · loading(스켈레톤) · empty(EmptyState) · error(ErrorState) ·
 * dialog(답변 FormDialog · 빈 답변 제출 차단 · 제출 중 close 차단).
 */
export function FeedbackAdminPage() {
  const { t } = useTranslation();
  const [statusFilter, setStatusFilter] = useState<FeedbackStatus | ''>('');
  const [showE2e, setShowE2e] = useState(false);
  const [page, setPage] = useState(0);
  const [toast, setToast] = useState<string | null>(null);
  const [answering, setAnswering] = useState<FeedbackResponse | null>(null);

  const excludeE2e = !showE2e;
  const listQuery = useFeedbackList({ status: statusFilter || undefined, excludeE2e, page, size: PAGE_SIZE });
  const answerMutation = useAnswerFeedback();

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<FeedbackAnswerFormValues>({
    resolver: zodResolver(feedbackAnswerSchema),
    defaultValues: { answer: '' },
  });

  useEffect(() => {
    if (answering) {
      reset({ answer: '' });
    }
  }, [answering, reset]);

  const showToast = (msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(null), 4000);
  };

  const isAnswering = answerMutation.isPending;

  const closeAnswerDialog = () => {
    if (isAnswering) return;
    setAnswering(null);
  };

  const onSubmitAnswer = (values: FeedbackAnswerFormValues) => {
    if (!answering) return;
    answerMutation.mutate(
      { id: answering.id, answer: values.answer },
      {
        onSuccess: () => {
          showToast(t('showcase.feedback.admin.toast.updated'));
          setAnswering(null);
        },
        onError: (err) => {
          const apiErr = err as unknown as FeedbackApiError;
          showToast(apiErr?.message ?? t('showcase.feedback.admin.toast.updateFailed'));
        },
      }
    );
  };

  const columns: DataTableColumn<FeedbackResponse>[] = [
    {
      key: 'kind',
      header: t('showcase.feedback.admin.column.kind'),
      render: (row) => t(`showcase.feedback.kind.${row.kind.toLowerCase()}`),
    },
    {
      key: 'moduleCode',
      header: t('showcase.feedback.admin.column.module'),
      render: (row) => row.moduleCode ?? t('showcase.feedback.admin.column.moduleNone'),
    },
    {
      key: 'body',
      header: t('showcase.feedback.admin.column.body'),
      render: (row) => (
        <span title={row.body} style={ellipsisStyle}>
          {row.body}
        </span>
      ),
    },
    {
      key: 'submitterEmail',
      header: t('showcase.feedback.admin.submitter'),
      render: (row) => row.submitterEmail ?? row.createdBy,
    },
    {
      key: 'createdAt',
      header: t('showcase.feedback.admin.column.createdAt'),
      render: (row) => new Date(row.createdAt).toLocaleString(),
    },
    {
      key: 'status',
      header: t('showcase.feedback.admin.column.status'),
      render: (row) => (
        <StatusPill tone={row.status === 'ANSWERED' ? 'success' : 'warning'}>
          {t(row.status === 'ANSWERED' ? 'showcase.feedback.status.answered' : 'showcase.feedback.status.received')}
        </StatusPill>
      ),
    },
    {
      key: 'answer',
      header: t('showcase.feedback.admin.column.answer'),
      render: (row) =>
        row.status === 'ANSWERED' && row.answer ? (
          <span
            title={`${row.answer}${row.answeredAt ? ` (${new Date(row.answeredAt).toLocaleString()})` : ''}`}
            style={ellipsisStyle}
          >
            {row.answer}
          </span>
        ) : (
          '-'
        ),
    },
    {
      key: 'actions',
      header: t('showcase.feedback.admin.column.actions'),
      render: (row) =>
        row.status === 'RECEIVED' ? (
          <button
            type="button"
            onClick={() => setAnswering(row)}
            data-testid={`feedback-answer-open-btn-${row.id}`}
            style={{
              padding: 'var(--spacing-1) var(--spacing-3)',
              background: 'var(--color-brand-primary)',
              color: 'var(--color-text-inverse)',
              border: 'none',
              borderRadius: 'var(--radius-sm)',
              fontSize: 'var(--font-size-xs)',
              fontWeight: 600,
              cursor: 'pointer',
              fontFamily: 'var(--font-family-sans)',
            }}
          >
            {t('showcase.feedback.action.markAnswered')}
          </button>
        ) : null,
    },
  ];

  const pageData = listQuery.data;
  const isLoading = listQuery.isLoading;
  const isError = listQuery.isError;
  const isEmpty = !isLoading && !isError && (pageData?.content.length ?? 0) === 0;
  const showTable = !isLoading && !isError && !isEmpty && !!pageData;

  return (
    <div style={{ maxWidth: 1280, margin: '0 auto', padding: 'var(--spacing-6)' }}>
      <PageHeader
        title={t('showcase.feedback.admin.title')}
        breadcrumb={
          <span>
            <Link to="/showcase" style={{ color: 'var(--color-text-secondary)' }}>
              {t('showcase.page.title')}
            </Link>{' '}
            / <span style={{ color: 'var(--color-text-primary)' }}>{t('showcase.feedback.admin.title')}</span>
          </span>
        }
        actions={
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-4)', flexWrap: 'wrap' }}>
            <label
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 'var(--spacing-2)',
                fontSize: 'var(--font-size-xs)',
                color: 'var(--color-text-secondary)',
                cursor: 'pointer',
              }}
            >
              <input
                type="checkbox"
                data-testid="feedback-show-e2e-toggle"
                checked={showE2e}
                onChange={(e) => {
                  setShowE2e(e.target.checked);
                  setPage(0);
                }}
              />
              {t('showcase.feedback.admin.showE2e')}
            </label>
            <label
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 'var(--spacing-2)',
                fontSize: 'var(--font-size-xs)',
                color: 'var(--color-text-secondary)',
              }}
            >
              {t('showcase.feedback.admin.filter.status.label')}
              <select
                value={statusFilter}
                onChange={(e) => {
                  setStatusFilter(e.target.value as FeedbackStatus | '');
                  setPage(0);
                }}
                style={{
                  padding: 'var(--spacing-1) var(--spacing-2)',
                  border: '1px solid var(--color-surface-border)',
                  borderRadius: 'var(--radius-md)',
                  fontSize: 'var(--font-size-sm)',
                  background: 'var(--color-surface-elevated)',
                  color: 'var(--color-text-primary)',
                  cursor: 'pointer',
                }}
              >
                <option value="">{t('showcase.feedback.admin.filter.status.all')}</option>
                <option value="RECEIVED">{t('showcase.feedback.status.received')}</option>
                <option value="ANSWERED">{t('showcase.feedback.status.answered')}</option>
              </select>
            </label>
          </div>
        }
      />

      {/* loading — DataTable 스켈레톤 */}
      {isLoading && (
        <div
          role="status"
          aria-busy="true"
          aria-label={t('showcase.feedback.admin.loading')}
          style={{
            border: '1px solid var(--color-surface-border)',
            borderRadius: 'var(--radius-md)',
            overflow: 'hidden',
          }}
        >
          {Array.from({ length: 5 }).map((_, i) => (
            <div
              key={i}
              style={{
                padding: 'var(--spacing-3) var(--spacing-4)',
                borderBottom: '1px solid var(--color-surface-border)',
              }}
            >
              <div
                style={{
                  height: 12,
                  width: `${50 + ((i * 13) % 40)}%`,
                  borderRadius: 'var(--radius-sm)',
                  background: 'var(--color-surface-elevated)',
                  animation: 'pulse 1.5s ease-in-out infinite',
                }}
              />
            </div>
          ))}
          <style>{`@keyframes pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.4; } }`}</style>
        </div>
      )}

      {/* error */}
      {isError && (
        <ErrorState
          title={t('showcase.feedback.admin.error.title')}
          message={t('showcase.feedback.admin.error.message')}
          onRetry={() => void listQuery.refetch()}
        />
      )}

      {/* empty — excludeE2e 로 0건이면 필터 안내 문구, 그 외 일반 empty 문구 */}
      {isEmpty && (
        <EmptyState
          title={t('showcase.feedback.admin.empty')}
          description={
            excludeE2e ? t('showcase.feedback.admin.emptyFiltered') : t('showcase.feedback.admin.emptyDesc')
          }
        />
      )}

      {/* default — DataTable */}
      {showTable && pageData && (
        <>
          <DataTable
            columns={columns}
            rows={pageData.content}
            rowKey={(r) => r.id}
            emptyMessage={t('showcase.feedback.admin.empty')}
          />

          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: 'var(--spacing-3) var(--spacing-4)',
              background: 'var(--color-surface-muted)',
              borderTop: '1px solid var(--color-surface-border)',
              flexWrap: 'wrap',
              gap: 'var(--spacing-2)',
            }}
          >
            <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-secondary)' }}>
              {t('showcase.feedback.admin.pagination.showing', {
                total: pageData.totalElements,
                page: pageData.number + 1,
                totalPages: Math.max(pageData.totalPages, 1),
              })}
            </span>
            <div style={{ display: 'inline-flex', gap: 'var(--spacing-1)' }}>
              <button
                type="button"
                disabled={pageData.first}
                onClick={() => setPage((p) => p - 1)}
                aria-label={t('showcase.feedback.admin.pagination.prev')}
                style={{
                  minWidth: 28,
                  height: 28,
                  padding: '0 var(--spacing-2)',
                  borderRadius: 'var(--radius-sm)',
                  background: 'var(--color-surface-elevated)',
                  border: '1px solid var(--color-surface-border)',
                  fontSize: 'var(--font-size-xs)',
                  cursor: pageData.first ? 'not-allowed' : 'pointer',
                  opacity: pageData.first ? 0.5 : 1,
                }}
              >
                &lt;
              </button>
              <button
                type="button"
                disabled={pageData.last}
                onClick={() => setPage((p) => p + 1)}
                aria-label={t('showcase.feedback.admin.pagination.next')}
                style={{
                  minWidth: 28,
                  height: 28,
                  padding: '0 var(--spacing-2)',
                  borderRadius: 'var(--radius-sm)',
                  background: 'var(--color-surface-elevated)',
                  border: '1px solid var(--color-surface-border)',
                  fontSize: 'var(--font-size-xs)',
                  cursor: pageData.last ? 'not-allowed' : 'pointer',
                  opacity: pageData.last ? 0.5 : 1,
                }}
              >
                &gt;
              </button>
            </div>
          </div>
        </>
      )}

      {/* dialog — 답변 등록 (queue #684 §R684-4(a)) */}
      {answering && (
        <FormDialog
          open
          title={t('showcase.feedback.admin.answer.title')}
          onClose={closeAnswerDialog}
          onSubmit={handleSubmit(onSubmitAnswer)}
          submitLabel={isAnswering ? t('showcase.feedback.submitting') : t('showcase.feedback.admin.answer.submit')}
          cancelLabel={t('showcase.feedback.cancel')}
          submitTestId="feedback-answer-submit-btn"
        >
          <div>
            <span style={labelStyle}>{t('showcase.feedback.admin.answer.original')}</span>
            <div style={readOnlyBodyStyle}>{answering.body}</div>

            <label htmlFor="feedback-answer-body" style={labelStyle}>
              {t('showcase.feedback.admin.answer.label')} *
            </label>
            <textarea
              id="feedback-answer-body"
              rows={5}
              maxLength={4000}
              placeholder={t('showcase.feedback.admin.answer.placeholder')}
              {...register('answer')}
              disabled={isAnswering}
              aria-invalid={!!errors.answer}
              aria-describedby={errors.answer ? 'feedback-answer-error' : undefined}
              style={errors.answer ? textareaErrorStyle : textareaStyle}
            />
            {errors.answer && (
              <span id="feedback-answer-error" role="alert" style={errorMsgStyle}>
                {errors.answer.message}
              </span>
            )}
          </div>
        </FormDialog>
      )}

      {/* 토스트 */}
      {toast !== null && (
        <div
          role="status"
          aria-live="polite"
          style={{
            position: 'fixed',
            bottom: 'var(--spacing-6)',
            right: 'var(--spacing-6)',
            zIndex: 1100,
            background: 'var(--color-surface-overlay, #1e1e2e)',
            color: 'var(--color-text-inverse)',
            padding: 'var(--spacing-3) var(--spacing-5)',
            borderRadius: 'var(--radius-md)',
            fontSize: 'var(--font-size-sm)',
            boxShadow: 'var(--shadow-lg)',
            maxWidth: 360,
          }}
        >
          {toast}
        </div>
      )}
    </div>
  );
}
